import { useState, useEffect, useRef } from "react";
import i18n from "../../../setup/translations/i18next";
import { useNotifications } from "../../modules/notifications";
import { NotificationDropdown } from "./notifications";
import * as lc from "../../modules/localstorage/index";
import { useTheme as useBrandTheme } from "src/setup/theme/useTheme";
import { useTheme } from "src/context/ThemeContext";
import { toAbsoluteUrl } from "src/resources/helpers/AssetHelpers";

const Header = () => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    i18n.language || "en"
  );
  const currentBrandTheme = useBrandTheme();
  const { theme, toggleTheme, isDesktop } = useTheme();
  const notificationsRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);

  // Use the notifications hook
  const { notifications, unreadCount, isLoading, actions } = useNotifications();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setNotificationsOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setLanguageOpen(false);
      }
      if (
        themeRef.current &&
        !themeRef.current.contains(event.target as Node)
      ) {
        setThemeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Listen for i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setSelectedLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  const handleLanguageSelect = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setLanguageOpen(false);
  };

  const handleThemeSelect = (selectedTheme: string) => {
    if (selectedTheme !== theme) {
      toggleTheme();
    }
    setThemeOpen(false);
  };

  const getDisplayLanguage = () => {
    switch (selectedLanguage) {
      case "en":
        return "EN";
      case "id":
        return "ID";
      case "es":
        return "ES";
      case "zh":
        return "中文";
      case "th":
        return "TH";
      default:
        return selectedLanguage.toUpperCase();
    }
  };

  const client = lc.getItemLC(lc.LCName.Client);
  const user = lc.getItemLC(lc.LCName.UserData);

  let userName = user && user.name ? user.name : "7days User";
  if (userName.length > 20) {
    userName = userName.substring(0, 17) + "...";
    console.log("userName after replace : ", userName);
  }

  return (
    <div
      className={`tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-[120] tw-flex tw-h-16 tw-shrink-0 tw-items-center tw-justify-between tw-border-b ${
        theme === "light" ? "tw-border-gray-200" : "tw-border-slate-600"
      } tw-bg-white dark:tw-bg-slate-900 tw-px-6 tw-pl-4`}
    >
      <div className="tw-flex tw-items-center tw-gap-3">
        <img
          src={toAbsoluteUrl("/media/logos/logo-sevendays-with-text.svg")}
          alt="SevenDays Logo"
          className={`tw-h-10 tw-w-auto ${theme === 'dark' ? 'tw-brightness-0 tw-invert' : ''}`}
        />
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-relative" ref={notificationsRef}>
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="tw-relative tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 dark:tw-text-slate-400 dark:hover:tw-bg-slate-600 dark:hover:tw-text-slate-200"
          >
            <span className="material-symbols-outlined tw-text-xl">
              notifications
            </span>
            {unreadCount > 0 && (
              <span className="tw-absolute tw-right-1 tw-top-1 tw-flex tw-h-2.5 tw-w-2.5 tw-items-center tw-justify-center">
                <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping tw-rounded-full tw-bg-red-400 tw-opacity-75"></span>
                <span className="tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-red-500"></span>
              </span>
            )}
          </button>
          <NotificationDropdown
            isOpen={notificationsOpen}
            notifications={notifications}
            unreadCount={unreadCount}
            isLoading={isLoading}
            onMarkAsRead={actions.markAsRead}
            onMarkAllAsRead={actions.markAllAsRead}
            onDelete={actions.deleteNotification}
            onViewAll={() => console.log("Navigate to notifications page")}
          />
        </div>
        <div className="tw-relative" ref={languageRef}>
          <button
            onClick={() => setLanguageOpen(!languageOpen)}
            className="tw-flex tw-h-9 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-px-2 tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 dark:tw-text-slate-400 dark:hover:tw-bg-slate-600 dark:hover:tw-text-slate-200"
          >
            <span className="material-symbols-outlined tw-text-xl">
              language
            </span>
            <span className="tw-text-sm tw-font-medium">
              {getDisplayLanguage()}
            </span>
          </button>
          <div
            className={`tw-absolute tw-right-0 tw-mt-2 tw-w-48 tw-origin-top-right tw-rounded-lg tw-bg-white dark:tw-bg-slate-800 tw-p-2 tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none ${
              languageOpen ? "tw-block" : "tw-hidden"
            }`}
          >
            <a
              onClick={(e) => {
                e.preventDefault();
                handleLanguageSelect("en");
              }}
              className={`tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${
                selectedLanguage === "en"
                  ? "tw-text-primary tw-bg-primary/10"
                  : "tw-text-gray-700 hover:tw-bg-gray-100"
              }`}
              href="#"
            >
              <span>English (US)</span>
              {selectedLanguage === "en" && (
                <span className="material-symbols-outlined tw-text-lg">
                  check
                </span>
              )}
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                handleLanguageSelect("id");
              }}
              className={`tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${
                selectedLanguage === "id"
                  ? "tw-text-primary tw-bg-primary/10"
                  : "tw-text-gray-700 hover:tw-bg-gray-100"
              }`}
              href="#"
            >
              <span>Bahasa Indonesia</span>
              {selectedLanguage === "id" && (
                <span className="material-symbols-outlined tw-text-lg">
                  check
                </span>
              )}
            </a>

            {/* Additional languages can be added here */}
            {/* Example: Spanish, Chinese, Thai 
            <a
              onClick={(e) => { e.preventDefault(); handleLanguageSelect('es'); }}
              className={`tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${selectedLanguage === 'es' ? 'tw-text-primary tw-bg-primary/10' : 'tw-text-gray-700 hover:tw-bg-gray-100'}`}
              href="#"
            >
              <span>Español</span>
              {selectedLanguage === 'es' && <span className="material-symbols-outlined tw-text-lg">check</span>}
            </a>
            <a
              onClick={(e) => { e.preventDefault(); handleLanguageSelect('zh'); }}
              className={`tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${selectedLanguage === 'zh' ? 'tw-text-primary tw-bg-primary/10' : 'tw-text-gray-700 hover:tw-bg-gray-100'}`}
              href="#"
            >
              <span>中文 (简体)</span>
              {selectedLanguage === 'zh' && <span className="material-symbols-outlined tw-text-lg">check</span>}
            </a>
            <a
              onClick={(e) => { e.preventDefault(); handleLanguageSelect('th'); }}
              className={`tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${selectedLanguage === 'th' ? 'tw-text-primary tw-bg-primary/10' : 'tw-text-gray-700 hover:tw-bg-gray-100'}`}
              href="#"
            >
              <span>ภาษาไทย</span>
              {selectedLanguage === 'th' && <span className="material-symbols-outlined tw-text-lg">check</span>}
            </a>
            */}
          </div>
        </div>
        {isDesktop && (
          <div className="tw-relative" ref={themeRef}>
            <button
              onClick={() => setThemeOpen(!themeOpen)}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 dark:tw-text-slate-400 dark:hover:tw-bg-slate-600 dark:hover:tw-text-slate-200"
            >
              <span className="material-symbols-outlined tw-text-xl">
                {theme === "dark" ? "dark_mode" : "light_mode"}
              </span>
            </button>
            <div
              className={`tw-absolute tw-right-0 tw-mt-2 tw-w-48 tw-origin-top-right tw-rounded-lg tw-bg-white dark:tw-bg-slate-800 tw-p-1 tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none ${
                themeOpen ? "tw-block" : "tw-hidden"
              }`}
            >
              <a
                onClick={(e) => {
                  e.preventDefault();
                  handleThemeSelect("light");
                }}
                className={`tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${
                  theme === "light"
                    ? "tw-bg-primary/10 tw-text-primary"
                    : "tw-text-gray-700 hover:tw-bg-gray-100 dark:tw-text-slate-300 dark:hover:tw-bg-slate-600"
                }`}
                href="#"
              >
                <span className="material-symbols-outlined tw-text-xl">
                  light_mode
                </span>
                <span>Light Theme</span>
              </a>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  handleThemeSelect("dark");
                }}
                className={`tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium ${
                  theme === "dark"
                    ? "tw-bg-primary/10 tw-text-primary"
                    : "tw-text-gray-700 hover:tw-bg-gray-100 dark:tw-text-slate-300 dark:hover:tw-bg-slate-600"
                }`}
                href="#"
              >
                <span className="material-symbols-outlined tw-text-xl">
                  dark_mode
                </span>
                <span>Dark Theme</span>
              </a>
            </div>
          </div>
        )}
        <div className="tw-ml-2 tw-h-8 tw-w-px tw-bg-gray-200 dark:tw-bg-slate-700"></div>

        <div className="tw-relative tw-ml-2">
          <button className="tw-flex tw-items-center tw-gap-3">
            <div
              className="tw-bg-center tw-bg-no-repeat tw-aspect-square tw-bg-cover tw-rounded-full tw-size-8"
              data-alt="User avatar"
              style={{
                backgroundImage:
                  "url('https://lh3.googleusercontent.com/aida-public/AB6AXuAf_Nb1i9ld0s_Q3knvVwwKug4a-GgCKNH5AS0BBGDrnsvdERCx1NbTpfKnppEkHX3DoREHrDRTh2kph13keFpQnupJRfUoerLUwCkoI70JYKeyLS44HxAegJOIgQWtPw1TeCLeo65yc1kTs9nGCDyQ0WNYI1eOZetI98Ppcp0OqjSF0i7rURpxoZqfJnCSbhTakvU1rC4MK7D7_Vuk4tSec24a2l_FlnDrKAVg5vLFak48Xdr_J3XEDYFfQFdgwOvRfAcvK4r1mT0')",
              }}
            ></div>
            <div className="tw-hidden tw-flex-col tw-items-start md:tw-flex">
              <p className="tw-text-sm tw-font-semibold tw-text-gray-800 dark:tw-text-slate-200">
                {userName}
              </p>
              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-slate-400">
                {client?.clientName ?? ""}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
